import type {
  BlogArticleSection,
  BlogCategory,
  BlogGameRef,
  BlogPost,
  StoreCategoryRef,
} from '../../../shared/models/blog.model';
import { publicOfferProductToUiProduct } from '../../../shared/utils/home-blocks.utils';
import type { PublicGameSectionSummary } from '../../game-section/data/game-section.model';
import type {
  BlogAppliedApi,
  BlogChipApi,
  BlogListResponse,
  BlogPostDetailApi,
  BlogSectionApi,
  BlogShowResponse,
} from './blog.api-model';
import {
  BLOG_FALLBACK_ACCENT_COLOR,
  blogImageUrl,
  toBlogChip,
  toBlogGameRef,
  toBlogPostCard,
} from './blog-card.mapper';
import type { BlogAppliedFilters, BlogArticleView, BlogListPage } from './blog-view.model';

// La tarjeta vive en `blog-card.mapper.ts` (P48) para que la landing de juego
// no arrastre al bundle inicial el resto de este módulo; se reexporta aquí para
// que el blog siga teniendo una sola puerta de mappers.
export {
  BLOG_FALLBACK_ACCENT_COLOR,
  BLOG_GENERAL_GAME,
  toBlogGameRef,
  toBlogPostCard,
} from './blog-card.mapper';

// Adaptador payload público del blog (snake_case) → vistas (camelCase), B119.
//
// Como `game-section.mappers.ts`, NO reimplementa reglas de omisión: la API ya
// decidió qué productos son publicables, el tope de 4 relacionados, qué
// categorías de tienda tienen enlace y qué slots trae cada sección. Aquí solo
// se traduce, se resuelven las URLs de imagen y se normaliza lo que el tipo de
// la vista exige.

/** Texto opcional: ausente o en blanco → `undefined` (la vista no lleva la clave). */
function optionalText(value: string | null | undefined): string | undefined {
  return value?.trim() ? value : undefined;
}

/** Juego del índice `GET /web/games` → pastilla de la barra de filtros (B120). */
export function gameSummaryToBlogGameRef(row: PublicGameSectionSummary): BlogGameRef {
  return {
    id: row.slug,
    name: row.name,
    slug: row.slug,
    accentColor: row.accent_color || BLOG_FALLBACK_ACCENT_COLOR,
  };
}

/** Sección del cuerpo: los slots solo como clave si la API los manda (B36 c). */
export function toBlogArticleSection(api: BlogSectionApi): BlogArticleSection {
  const section: BlogArticleSection = {
    id: api.anchor,
    heading: api.heading,
    bodyHtml: api.body_html ?? '',
  };
  if (optionalText(api.image_url)) section.imageUrl = blogImageUrl(api.image_url);
  const imageAlt = optionalText(api.image_alt);
  if (imageAlt) section.imageAlt = imageAlt;
  const imageCaption = optionalText(api.image_caption);
  if (imageCaption) section.imageCaption = imageCaption;
  const quote = optionalText(api.quote);
  if (quote) section.quote = quote;
  const videoUrl = optionalText(api.video_url);
  if (videoUrl) section.videoUrl = videoUrl;
  const videoCaption = optionalText(api.video_caption);
  if (videoCaption) section.videoCaption = videoCaption;
  if (api.inline_product) section.inlineProduct = publicOfferProductToUiProduct(api.inline_product);
  return section;
}

function toStoreCategoryRef(api: BlogChipApi): StoreCategoryRef {
  return { id: String(api.id), name: api.name, slug: api.slug };
}

/**
 * Ficha (B79). Los relacionados llegan ya filtrados, en el orden curado y con
 * el tope de 4 (B36, B69): aquí ni se filtra ni se recorta. Sin secciones no
 * hay `content` y la página pinta «en preparación».
 */
export function toBlogPostDetail(api: BlogPostDetailApi): BlogPost {
  const sections = (api.sections ?? []).map(toBlogArticleSection);
  const post: BlogPost = {
    ...toBlogPostCard(api),
    relatedProducts: (api.related_products ?? []).map(publicOfferProductToUiProduct),
    authorName: api.author?.display_name ?? '',
    authorRole: api.author?.role ?? '',
    authorAvatarUrl: blogImageUrl(api.author?.avatar_url),
    relatedStoreCategories: (api.store_categories ?? []).map(toStoreCategoryRef),
  };
  if (sections.length > 0) post.content = { sections };
  const bio = optionalText(api.author?.bio);
  if (bio) post.authorBio = bio;
  if (api.author?.article_count != null) post.authorArticleCount = Number(api.author.article_count);
  const metaTitle = optionalText(api.meta_title);
  if (metaTitle) post.metaTitle = metaTitle;
  const metaDescription = optionalText(api.meta_description);
  if (metaDescription) post.metaDescription = metaDescription;
  if (optionalText(api.og_image_url)) post.ogImageUrl = blogImageUrl(api.og_image_url);
  return post;
}

/** `GET /web/blog/posts/{slug}` → ficha con su navegación (B85). */
export function toBlogArticleView(res: BlogShowResponse): BlogArticleView {
  return {
    post: toBlogPostDetail(res.data),
    prev: res.prev ? toBlogPostCard(res.prev) : null,
    next: res.next ? toBlogPostCard(res.next) : null,
    related: (res.related ?? []).map(toBlogPostCard),
  };
}

function toAppliedFilters(api: BlogAppliedApi | null | undefined): BlogAppliedFilters {
  const applied: BlogAppliedFilters = {};
  if (api?.game?.slug) applied.game = toBlogGameRef(api.game);
  if (api?.category) applied.category = toBlogChip(api.category);
  if (api?.tag) applied.tag = toBlogChip(api.tag);
  if (typeof api?.search === 'string' && api.search !== '') applied.search = api.search;
  return applied;
}

/**
 * `GET /web/blog/posts` → página del listado. `total` es `meta.total` tal
 * cual: sin filtros NO cuenta el destacado cuando viaja aparte (E8), por eso
 * el contador solo se pinta con filtros (B131).
 */
export function toBlogListPage(res: BlogListResponse): BlogListPage {
  return {
    posts: (res.data ?? []).map(toBlogPostCard),
    featured: res.featured ? toBlogPostCard(res.featured) : null,
    total: Number(res.meta.total),
    page: Number(res.meta.page),
    totalPages: Number(res.meta.total_pages),
    applied: toAppliedFilters(res.applied),
  };
}

/** Categorías o etiquetas de la barra de filtros. */
export function toBlogChips(chips: readonly BlogChipApi[] | null | undefined): BlogCategory[] {
  return (chips ?? []).map(toBlogChip);
}
